import { Trans } from '@lingui/macro';
import AccessDisk from 'components/AccessDisk/AccessDisk';
import { useScreenSizeRatio } from 'hooks/useScreenSizeRatio';
import React, { useState } from 'react';
import { Outlet, useOutletContext } from 'react-router-dom';
import { useAppDispatch } from 'state/hooks';
import { useScreenCursorState } from 'state/phonebooth/hooks';
import { updateScreenCursorState } from 'state/phonebooth/slice';
import { Transmission as TransmissionSound } from 'types/transmission';

import * as S from './PhoneBoothScreen.styles';
import ScreenCursor from './ScreenCursor';

type PhoneboothContext = {
  transmission: TransmissionSound;
  onGenerateAccessDisk: (handle: string) => void;
  onFinishedGenerating: () => void;
  onHideAccessDisk: () => void;
};

const PhoneBoothScreen: React.FC<{ transmission: TransmissionSound }> = ({ transmission }) => {
  const dispatch = useAppDispatch();
  const mouseState = useScreenCursorState();

  const [showMobileModal, setShowMobileModal] = useState(false);
  const sizeRatio = useScreenSizeRatio();

  const [nameToPrint, setNameToPrint] = useState<string | undefined>(undefined);
  const [finishedPrint, setFinishedPrint] = useState<boolean>(false);

  return (
    <>
      <ScreenCursor mouseState={mouseState} />
      <S.MobileScreenWrapper sizeRatio={sizeRatio} onClick={() => setShowMobileModal(true)}>
        <S.ScanLine />
        <Trans>TAP HERE TO START...</Trans>
      </S.MobileScreenWrapper>
      {showMobileModal && <S.BackgroundModal onClick={() => setShowMobileModal(false)} />}
      <S.ScreenWrapper
        sizeRatio={sizeRatio}
        onMouseEnter={() => {
          dispatch(updateScreenCursorState('hover'));
        }}
        onMouseLeave={() => dispatch(updateScreenCursorState('none'))}
        showMobileModal={showMobileModal}
      >
        <S.ScanLine />
        <Outlet
          context={{
            transmission,
            onGenerateAccessDisk: (handle: string) => setNameToPrint(handle),
            onFinishedGenerating: () => setFinishedPrint(true),
            onHideAccessDisk: () => {
              setNameToPrint(undefined);
              setFinishedPrint(false);
            },
          }}
        />
      </S.ScreenWrapper>

      {nameToPrint && <AccessDisk text={nameToPrint} isGenerating={!finishedPrint} />}
    </>
  );
};

export function usePhoneboothContext() {
  return useOutletContext<PhoneboothContext>();
}

export default PhoneBoothScreen;
